import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import { Entry, FlexibleContent, IPage } from '@belong/types';
import { parseLinkData } from '../helpers';
import { FAccordionRow } from './accordionRow.factory';
import { FCollectionGeneric } from './collectionGeneric.factory';
import {
  FElementCountryList,
  FElementEmbeddedInformationModal,
  FElementNbnOrderSummary,
  FLink,
  FPaymentMethod,
  FSystemIcon
} from './elements';
import { FGenericScreen } from './genericScreen.factory';
import { FEmail, FGenericInput, FPassword } from './inputs';
import { FGenericInputGroup } from './inputs/genericInputGroup.factory';
import {
  FLinkListItem,
  FMoleculeAlert,
  FMoleculeAsset,
  FMoleculeCallout,
  FMoleculeInformationPanel,
  FMoleculeMediaCaptionBlock,
  FMoleculeModal,
  FMoleculeProgressIndicator,
  FMoleculeRichText,
  FMoleculeText
} from './molecules';
import {
  FOrganismAddressCapture,
  FOrganismNbnServiceTile,
  FOrganismPromoCard,
  FOrganismSecondaryTile,
  FOrganismServiceLinks,
  FOrganismVerificationCodeCapture
} from './organisms';
import { FPageAlert } from './pageAlert.factory';
import { FlexibleComponentMap, parseFlexibleContent } from './parseFlexibleContent';
import { FSectionInfo, FSectionPageTitle } from './sections';

export type AnySectionData = Record<string, any>;

/**
 * `sectionParser` can have some app-specific behaviours, so we require each app
 * to implement it.
 */
export type SectionParser = (section: any) => Promise<AnySectionData>;
export type ContentPatcher<TContent = FlexibleContent> = (
  pageContent: IPage<TContent>,
  pageUrl: string,
  correlationId: string
) => IPage<TContent>;

export const CMS_FACTORY_MAP = new Map([
  ['collectionGeneric', { factory: FCollectionGeneric }],
  ['countryList', { factory: FElementCountryList }],
  ['elementAccordionRow', { factory: FAccordionRow }],
  ['elementEmbeddedInformationModal', { factory: FElementEmbeddedInformationModal }],
  ['elementLink', { factory: FLink }],
  ['elementNbnOrderSummary', { factory: FElementNbnOrderSummary }],
  ['elementPaymentMethod', { factory: FPaymentMethod }],
  ['elementSystemIcon', { factory: FSystemIcon }],
  ['inputEmail', { factory: FEmail }],
  ['inputGeneric', { factory: FGenericInput }],
  ['inputGenericGroup', { factory: FGenericInputGroup }],
  ['inputPassword', { factory: FPassword }],
  ['moleculeAlert', { factory: FMoleculeAlert }],
  ['moleculeAsset', { factory: FMoleculeAsset }],
  ['moleculeCallout', { factory: FMoleculeCallout }],
  ['moleculeInformationPanel', { factory: FMoleculeInformationPanel }],
  ['moleculeLinkListItem', { factory: FLinkListItem }],
  ['moleculeMediaCaptionBlock', { factory: FMoleculeMediaCaptionBlock }],
  ['moleculeModal', { factory: FMoleculeModal }],
  ['moleculeProgressIndicator', { factory: FMoleculeProgressIndicator }],
  ['moleculeRichText', { factory: FMoleculeRichText }],
  ['moleculeText', { factory: FMoleculeText }],
  ['organismAddressCapture', { factory: FOrganismAddressCapture }],
  ['organismPromoCard', { factory: FOrganismPromoCard }],
  ['organismNbnService', { factory: FOrganismNbnServiceTile }],
  ['organismSecondaryTile', { factory: FOrganismSecondaryTile }],
  ['organismServiceLinks', { factory: FOrganismServiceLinks }],
  ['organismVerificationCodeCapture', { factory: FOrganismVerificationCodeCapture }],
  ['screenGeneric', { factory: FGenericScreen }],
  ['sectionInfo', { factory: FSectionInfo }],
  ['sectionPageTitle', { factory: FSectionPageTitle }]
]);

export const FPage = async <TContent = FlexibleContent>(
  pageData: any,
  sectionParser: SectionParser,
  flexibleComponentsMap: FlexibleComponentMap = CMS_FACTORY_MAP
): Promise<IPage<TContent>> => {
  const data: Entry<any> = pageData[0];

  const {
    alert,
    breadcrumbs,
    content,
    description,
    enableLiveChat,
    flowName,
    journeyName,
    keywords,
    noFollow,
    noIndex,
    pageName,
    pageTitle,
    pageUrl,
    screen,
    sections
  } = data.fields;
  const url = parseLinkData(pageUrl).href;
  const result: IPage<TContent> = {
    breadcrumbs: breadcrumbs?.map(FLink),
    description: documentToPlainTextString(description),
    flowName,
    identifier: url,
    journeyName,
    keywords,
    meta: {
      entryId: data.sys?.id
    },
    noFollow,
    noIndex,
    pageName,
    pageTitle,
    pageUrl: url,
    screen,
    sections: await Promise.all((sections ?? []).map(sectionParser)),
    content: parseFlexibleContent<TContent>(content ?? [], flexibleComponentsMap),
    shouldHaveLiveChat: enableLiveChat ?? false
  };
  if (alert) {
    result.alert = FPageAlert(alert);
  }

  return result;
};
